import React, { useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { PermissionAccessMaster, PermissionGroupMaster } from '../../../../api/strapiPermissionMaster';
import { useAuth } from '../../../../auth/AuthContext';
import {
  useCreatePermissionAccessMutation,
  useCreatePermissionGroupMutation,
  useDeletePermissionAccessMutation,
  useDeletePermissionGroupMutation,
  usePermissionMasterQuery,
  useUpdatePermissionAccessMutation,
  useUpdatePermissionGroupMutation,
} from '../../../../query/hooks';

type GroupDialogState =
  | { open: false }
  | { open: true; mode: 'create' }
  | { open: true; mode: 'edit'; group: PermissionGroupMaster };

type AccessDialogState =
  | { open: false }
  | {
      open: true;
      mode: 'create';
      group: PermissionGroupMaster;
    }
  | {
      open: true;
      mode: 'edit';
      group: PermissionGroupMaster;
      access: PermissionAccessMaster;
    };

type ConfirmState =
  | { open: false }
  | { open: true; kind: 'group'; group: PermissionGroupMaster }
  | { open: true; kind: 'access'; access: PermissionAccessMaster; groupLabel: string };

export function ManageMasterTabScreen() {
  const { jwt } = useAuth();
  const { data: groups, isPending, isError, error, refetch } = usePermissionMasterQuery(jwt);
  const createGroup = useCreatePermissionGroupMutation(jwt);
  const updateGroup = useUpdatePermissionGroupMutation(jwt);
  const deleteGroup = useDeletePermissionGroupMutation(jwt);
  const createAccess = useCreatePermissionAccessMutation(jwt);
  const updateAccess = useUpdatePermissionAccessMutation(jwt);
  const deleteAccess = useDeletePermissionAccessMutation(jwt);

  const [groupDialog, setGroupDialog] = useState<GroupDialogState>({ open: false });
  const [accessDialog, setAccessDialog] = useState<AccessDialogState>({ open: false });
  const [confirm, setConfirm] = useState<ConfirmState>({ open: false });

  const [groupNameInput, setGroupNameInput] = useState('');
  const groupNameValid = groupNameInput.trim().length > 0;

  const [accessCodeInput, setAccessCodeInput] = useState('');
  const [accessNameInput, setAccessNameInput] = useState('');
  const [accessDescInput, setAccessDescInput] = useState('');
  const [accessNavPathInput, setAccessNavPathInput] = useState('');
  const accessFormValid =
    accessCodeInput.trim().length > 0 && accessNameInput.trim().length > 0;

  const sortedGroups = useMemo(
    () => [...(groups ?? [])].sort((a, b) => a.group_name.localeCompare(b.group_name)),
    [groups],
  );

  function openCreateGroup() {
    setGroupNameInput('');
    setGroupDialog({ open: true, mode: 'create' });
  }

  function openEditGroup(group: PermissionGroupMaster) {
    setGroupNameInput(group.group_name);
    setGroupDialog({ open: true, mode: 'edit', group });
  }

  function openCreateAccess(group: PermissionGroupMaster) {
    setAccessCodeInput('');
    setAccessNameInput('');
    setAccessDescInput('');
    setAccessNavPathInput('');
    setAccessDialog({ open: true, mode: 'create', group });
  }

  function openEditAccess(group: PermissionGroupMaster, access: PermissionAccessMaster) {
    setAccessCodeInput(access.access_code);
    setAccessNameInput(access.access_name);
    setAccessDescInput(access.description);
    setAccessNavPathInput(access.nav_path ?? '');
    setAccessDialog({ open: true, mode: 'edit', group, access });
  }

  async function submitGroupDialog() {
    if (!groupNameValid) return;
    try {
      if (groupDialog.open && groupDialog.mode === 'create') {
        await createGroup.mutateAsync(groupNameInput);
      } else if (groupDialog.open && groupDialog.mode === 'edit') {
        const docId = groupDialog.group.documentId;
        if (!docId) throw new Error('Group is missing documentId');
        await updateGroup.mutateAsync({ documentId: docId, group_name: groupNameInput });
      }
      setGroupDialog({ open: false });
    } catch {
      /* surfaced below */
    }
  }

  async function submitAccessDialog() {
    if (!accessFormValid) return;
    try {
      if (accessDialog.open && accessDialog.mode === 'create') {
        const g = accessDialog.group;
        const docId = g.documentId;
        if (!docId) throw new Error('Group is missing documentId');
        await createAccess.mutateAsync({
          permissionGroupDocumentId: docId,
          permissionGroupNumericId: g.numericId,
          access_code: accessCodeInput,
          access_name: accessNameInput,
          description: accessDescInput.trim() || undefined,
          nav_path: accessNavPathInput.trim() || undefined,
        });
      } else if (accessDialog.open && accessDialog.mode === 'edit') {
        const docId = accessDialog.access.documentId;
        if (!docId) throw new Error('Access is missing documentId');
        await updateAccess.mutateAsync({
          documentId: docId,
          access_code: accessCodeInput,
          access_name: accessNameInput,
          description: accessDescInput,
          nav_path: accessNavPathInput,
        });
      }
      setAccessDialog({ open: false });
    } catch {
      /* surfaced */
    }
  }

  async function runConfirmedDelete() {
    if (!confirm.open) return;
    try {
      if (confirm.kind === 'group') {
        const docId = confirm.group.documentId;
        if (!docId) throw new Error('Group is missing documentId');
        await deleteGroup.mutateAsync(docId);
      } else {
        const docId = confirm.access.documentId;
        if (!docId) throw new Error('Access is missing documentId');
        await deleteAccess.mutateAsync(docId);
      }
      setConfirm({ open: false });
    } catch {
      /* surfaced */
    }
  }

  const savingGroup =
    createGroup.isPending || updateGroup.isPending || deleteGroup.isPending;
  const savingAccess =
    createAccess.isPending || updateAccess.isPending || deleteAccess.isPending;

  return (
    <Stack spacing={1} sx={{ width: '100%', flex: 1, minHeight: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreateGroup}>
          Add permission group
        </Button>
        <Button size="small" onClick={() => void refetch()}>
          Refresh
        </Button>
      </Stack>

      {isPending && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      )}

      {isError && (
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Failed to load permission data.'}
        </Alert>
      )}

      {!isPending && !isError && sortedGroups.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No permission groups yet. Create one to add accesses.
        </Typography>
      )}

      {!isPending &&
        sortedGroups.map((group) => {
          const gDoc = group.documentId;
          const sortedAccesses = [...group.accesses].sort((a, b) =>
            a.access_code.localeCompare(b.access_code),
          );
          const canDeleteGroup = group.accesses.length === 0 && Boolean(gDoc);
          return (
            <Accordion key={gDoc ?? group.group_name} defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 1, flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {group.group_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({sortedAccesses.length} access{sortedAccesses.length === 1 ? '' : 'es'})
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => openCreateAccess(group)}
                      disabled={!gDoc}
                    >
                      Add access
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => openEditGroup(group)}
                      disabled={!gDoc}
                    >
                      Rename group
                    </Button>
                    <Tooltip
                      title={
                        group.accesses.length > 0
                          ? 'Remove all accesses before deleting this group'
                          : 'Delete group'
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          disabled={!canDeleteGroup}
                          onClick={() => setConfirm({ open: true, kind: 'group', group })}
                        >
                          Delete group
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                  {!gDoc && (
                    <Typography variant="caption" color="error">
                      This group has no document id; save it in Strapi admin or refresh.
                    </Typography>
                  )}
                  {sortedAccesses.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No accesses in this group.
                    </Typography>
                  ) : (
                    <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      {sortedAccesses.map((a) => {
                        const aDoc = a.documentId;
                        return (
                          <ListItem key={aDoc ?? a.access_code} divider>
                            <ListItemText
                              primary={
                                <>
                                  <Typography component="span" variant="body2" fontWeight={600}>
                                    {a.access_code}
                                  </Typography>{' '}
                                  <Typography component="span" variant="body2" color="text.secondary">
                                    — {a.access_name}
                                  </Typography>
                                </>
                              }
                              secondary={[
                                a.nav_path?.trim() ? `Nav: ${a.nav_path.trim()}` : null,
                                a.description?.trim() ? a.description : null,
                              ]
                                .filter(Boolean)
                                .join(' · ') || undefined}
                            />
                            <ListItemSecondaryAction>
                              <Tooltip title="Edit">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  aria-label={`Edit ${a.access_code}`}
                                  disabled={!aDoc}
                                  onClick={() => openEditAccess(group, a)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  aria-label={`Delete ${a.access_code}`}
                                  disabled={!aDoc}
                                  onClick={() =>
                                    setConfirm({
                                      open: true,
                                      kind: 'access',
                                      access: a,
                                      groupLabel: group.group_name,
                                    })
                                  }
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}

      <Dialog
        open={groupDialog.open}
        onClose={() => setGroupDialog({ open: false })}
        fullWidth
        maxWidth="xs"
        aria-labelledby="perm-group-dialog-title"
      >
        <DialogTitle id="perm-group-dialog-title">
          {groupDialog.open && groupDialog.mode === 'edit' ? 'Rename permission group' : 'New permission group'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Group name"
              value={groupNameInput}
              onChange={(e) => setGroupNameInput(e.target.value)}
              fullWidth
              required
              size="small"
              autoFocus
              helperText="Unique name (e.g. marketing, superadmin)."
            />
            {(createGroup.isError || updateGroup.isError) && (
              <Typography variant="body2" color="error">
                {(() => {
                  const e = createGroup.error ?? updateGroup.error;
                  return e instanceof Error ? e.message : 'Request failed';
                })()}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGroupDialog({ open: false })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void submitGroupDialog()}
            disabled={!groupNameValid || savingGroup}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={accessDialog.open}
        onClose={() => setAccessDialog({ open: false })}
        fullWidth
        maxWidth="sm"
        aria-labelledby="perm-access-dialog-title"
      >
        <DialogTitle id="perm-access-dialog-title">
          {accessDialog.open && accessDialog.mode === 'edit' ? 'Edit permission access' : 'New permission access'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {accessDialog.open && (
              <Typography variant="body2" color="text.secondary">
                Group: <strong>{accessDialog.group.group_name}</strong>
              </Typography>
            )}
            <TextField
              label="Access code"
              value={accessCodeInput}
              onChange={(e) => setAccessCodeInput(e.target.value)}
              fullWidth
              required
              size="small"
              disabled={accessDialog.open && accessDialog.mode === 'edit'}
              helperText={
                accessDialog.open && accessDialog.mode === 'edit'
                  ? 'Code cannot be changed (unique key).'
                  : 'Unique key (e.g. can_view_quotation).'
              }
            />
            <TextField
              label="Display name"
              value={accessNameInput}
              onChange={(e) => setAccessNameInput(e.target.value)}
              fullWidth
              required
              size="small"
            />
            <TextField
              label="Description"
              value={accessDescInput}
              onChange={(e) => setAccessDescInput(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
            <TextField
              label="Nav path"
              value={accessNavPathInput}
              onChange={(e) => setAccessNavPathInput(e.target.value)}
              fullWidth
              size="small"
              placeholder="/quotations"
              helperText="App route for the left drawer (e.g. /dashboard). Leading slash optional."
            />
            {(createAccess.isError || updateAccess.isError) && (
              <Typography variant="body2" color="error">
                {(() => {
                  const e = createAccess.error ?? updateAccess.error;
                  return e instanceof Error ? e.message : 'Request failed';
                })()}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAccessDialog({ open: false })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void submitAccessDialog()}
            disabled={!accessFormValid || savingAccess}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        aria-labelledby="perm-delete-title"
      >
        <DialogTitle id="perm-delete-title">
          {confirm.open && confirm.kind === 'group' ? 'Delete permission group?' : 'Delete permission access?'}
        </DialogTitle>
        <DialogContent>
          {confirm.open && confirm.kind === 'group' && (
            <Typography variant="body2">
              Delete group <strong>{confirm.group.group_name}</strong>? This cannot be undone.
            </Typography>
          )}
          {confirm.open && confirm.kind === 'access' && (
            <Typography variant="body2">
              Delete <strong>{confirm.access.access_code}</strong> from {confirm.groupLabel}? Presets that use this
              access may need updating.
            </Typography>
          )}
          {(deleteGroup.isError || deleteAccess.isError) && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {(() => {
                const e = deleteGroup.error ?? deleteAccess.error;
                return e instanceof Error ? e.message : 'Delete failed';
              })()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm({ open: false })}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void runConfirmedDelete()}
            disabled={deleteGroup.isPending || deleteAccess.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
